/**
 * A new conversation: the welcome, the composer, and where to go from here.
 *
 * The access notice and the three cards read the server's own answers about
 * this workspace — `/platform/products` — rather than rules reassembled here.
 * "Get access" goes to the CenoX AI product page, which runs the real trial,
 * activation or upgrade flow; nothing on this screen activates anything.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, BarChart3, ImageIcon, Lock, Workflow } from 'lucide-react';
import type { PlatformProduct } from '../../../lib/platformProducts';
import { CenoxMark } from '../../../components/platform/CenoxMark';
import { NoWorkspaceNotice } from '../../../components/platform/NoWorkspaceNotice';
import { cn } from '../../../components/ui';

const card =
  'group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition-all';
const live = 'hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-brand-500';

function CardBody({ icon, title, hint }: { icon: ReactNode; title: string; hint: string }): ReactNode {
  return (
    <>
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors group-hover:bg-brand-50 group-hover:text-brand-600">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-900">{title}</span>
        <span className="block truncate text-xs text-slate-500">{hint}</span>
      </span>
    </>
  );
}

export function ChatWelcome({
  firstName,
  composer,
  aiReady,
  aiKnown,
  hasWorkspace,
  ai,
  automation,
  onAnalyse,
}: {
  firstName?: string;
  composer: ReactNode;
  aiReady: boolean;
  /** The products answer has arrived; until then nothing is offered or refused. */
  aiKnown: boolean;
  hasWorkspace: boolean;
  ai?: PlatformProduct;
  automation?: PlatformProduct;
  onAnalyse: () => void;
}): ReactNode {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-1 pb-10 pt-6 sm:pt-12">
      <CenoxMark className="size-12" />
      <h1 className="mt-5 text-center text-2xl font-semibold tracking-tight text-slate-900 sm:text-[2rem] sm:leading-tight">
        What should we work on{firstName ? `, ${firstName}` : ''}?
      </h1>
      <p className="mt-2 text-center text-sm text-slate-500 sm:text-base">
        Build, research, analyse, create and automate with CenoX.
      </p>

      <div className="mt-8 w-full">{composer}</div>

      {/*
        Said plainly, in the place the thing would have happened. Hidden the
        moment the workspace holds CenoX AI, because the server says so.
      */}
      {aiKnown && !aiReady && hasWorkspace && (
        <section
          aria-label="CenoX AI access"
          className="mt-5 flex w-full flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-brand-50/60 p-4 sm:flex-nowrap"
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 shadow-sm">
            <Lock className="size-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-slate-900">CenoX AI is not part of this workspace yet.</span>
            <span className="block text-xs text-slate-500">Chat, analysis and answers from your own data come with it.</span>
          </span>
          <Link
            to="/ai"
            className="bg-cx-gradient inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm sm:w-auto sm:py-2 font-medium text-white shadow-sm transition-all hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            {ai?.catalogueStatus === 'available' ? 'Get access' : 'Learn more'}
          </Link>
        </section>
      )}
      {aiKnown && !aiReady && !hasWorkspace && (
        <NoWorkspaceNotice className="mt-5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3" />
      )}

      <div className="mt-6 grid w-full gap-3 sm:grid-cols-3" aria-label="Get started">
        {aiReady ? (
          <button type="button" onClick={onAnalyse} className={cn(card, live)}>
            <CardBody icon={<BarChart3 className="size-5" aria-hidden />} title="Analyze data" hint="With CenoX AI" />
          </button>
        ) : (
          <Link to="/ai" className={cn(card, live)}>
            <CardBody icon={<BarChart3 className="size-5" aria-hidden />} title="Analyze data" hint="With CenoX AI" />
          </Link>
        )}

        <div aria-disabled="true" title="Image generation is not built yet" className={cn(card, 'cursor-not-allowed opacity-60')}>
          <CardBody icon={<ImageIcon className="size-5" aria-hidden />} title="Generate images" hint="Not built yet" />
        </div>

        <Link to={automation ? `/${automation.routeSlug}` : '/automation'} className={cn(card, live)}>
          <CardBody
            icon={<Workflow className="size-5" aria-hidden />}
            title="Run a workflow"
            hint={automation?.availableAction === 'open' ? 'Open CenoX Automation' : 'In CenoX Automation'}
          />
          <ArrowUpRight className="size-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500" aria-hidden />
        </Link>
      </div>
    </div>
  );
}
